import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { useSelector } from "react-redux";
import { isClientMissing, validateManagerObject } from "utils/userValidationUtil";
import { emailFieldProps, normalizeEmail } from "utils/emailValidationUtil";
import {
  addManagerToBackend,
  updateManagerToBackend,
  loadEligibleParents,
  loadEligibleParentsForUser,
} from "app/services/manager.service";
import { loadUsersByRoleFromBackend } from "app/services/user.service";
import * as AppConstants from "AppConstants";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  clientEmail: "",
  userId: "",
  clientId: "",
  parentManagerId: "",
};

// A manager is one row per client, so several rows carry the same person's name. The picker
// shows the client next to the name — reusing the "CODE - Name" client convention used across
// the app — so the intended row can be told apart. @since NEXT_VERSION
const managerOptionLabel = (manager) => {
  const client =
    manager.clientCode && manager.clientName
      ? `${manager.clientCode} - ${manager.clientName}`
      : manager.clientName ?? manager.clientCode ?? "";
  return client ? `${manager.name} (${client})` : manager.name;
};

const AddManagerDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); //"error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const [managerUsers, setManagerUsers] = useState([]);
  const [eligibleParents, setEligibleParents] = useState([]);
  const clients = useSelector((state) => state.client.rows);

  useEffect(() => {
    setMessageOpen(false);
    setMessage("");
    setSeverity("info");
    if (!open) return;
    loadUsersByRoleFromBackend(AppConstants.ROLE_EMPLOYEE_MANAGER)
      .then((res) => setManagerUsers(Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        setManagerUsers([]);
        handleAxiosError(error);
      });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editObject) {
      if (isClientMissing(clients, editObject.clientId)) {
        setData({
          id: parseInt(editObject.id),
          clientEmail: editObject.clientEmail,
          userId: parseInt(editObject.userId),
          clientId: "",
          parentManagerId: editObject.parentManagerId ?? "",
        });
        setErrorAction("The client for this manager no longer exists. Please select a client.");
        return;
      }
      setData({
        id: parseInt(editObject.id),
        clientEmail: editObject.clientEmail,
        userId: parseInt(editObject.userId),
        clientId: parseInt(editObject.clientId),
        parentManagerId: editObject.parentManagerId ?? "",
      });
    } else {
      setData(obj);
    }
  }, [open, editObject, clients]);

  // Editing keys the candidate set on the manager row; creating keys it on the chosen user,
  // because the new manager has no id yet and eligibility turns on which person is mapped.
  useEffect(() => {
    if (!open) {
      setEligibleParents([]);
      return;
    }
    const managerId = editObject?.id;
    if (!managerId && !data.userId) {
      setEligibleParents([]);
      return;
    }
    const request = managerId ? loadEligibleParents(managerId) : loadEligibleParentsForUser(data.userId);
    request
      .then((res) => setEligibleParents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEligibleParents([]));
  }, [open, editObject, data.userId]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveManagerInBackend = async () => {
    const payload = trimTextFields({ ...data, clientEmail: normalizeEmail(data.clientEmail) });
    if (validateManagerObject(payload) && !editObject) await addManagerInvoker(payload);
    else if (validateManagerObject(payload) && editObject) await updateManagerInvoker(payload);
    else {
      setErrorAction("One or more required fields for a Manager are missing or having invalid value");
    }
  };

  const addManagerInvoker = async (userObject) => {
    await addManagerToBackend(userObject)
      .then((data) => {
        setSuccessAction("Manager added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add manager. Please try again.");
      });
  };

  const updateManagerInvoker = async (data) => {
    const editObjectId = data.id;
    await updateManagerToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Manager Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update manager. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update InOpTra's Manager` : `Add New InOpTra's Manager`}</DialogTitle>
      <DialogContent>
        {/* <DialogContentText></DialogContentText> */}
        <MDBox my={2}>
          <MasterDataSelect
            id="client"
            label="Client / Account"
            placeholder="Select a client"
            options={clients}
            value={data?.clientId || ""}
            onChange={(e) => setData({ ...data, clientId: parseInt(e.target.value, 10) || null })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="user"
            label="InOpTra's Client Manager"
            placeholder="Select a user"
            options={managerUsers}
            getOptionLabel={(managerUser) => managerUser.fullName}
            value={data?.userId || ""}
            onChange={(e) => {
              const userId = parseInt(e.target.value, 10) || null;
              // On create the candidate list is per person, so a parent picked for the previous
              // user may no longer be eligible. Editing loads candidates by manager id instead.
              setData({ ...data, userId, ...(editObject ? {} : { parentManagerId: "" }) });
            }}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="parentManager"
            label="Reports to (Manager)"
            placeholder="None"
            placeholderItalic={false}
            options={eligibleParents}
            getOptionLabel={managerOptionLabel}
            value={data?.parentManagerId || ""}
            onChange={(e) =>
              setData({ ...data, parentManagerId: parseInt(e.target.value, 10) || null })
            }
            helperText={data?.userId ? undefined : "Select a user first"}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="clientEmail"
            label="Email"
            fullWidth
            variant="outlined"
            value={data?.clientEmail || ""}
            onBlur={trimOnBlur(setData, "clientEmail")}
            onChange={(e) => setData({ ...data, clientEmail: e.target.value })}
            {...emailFieldProps(data?.clientEmail)}
            helperText="*Required"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveManagerInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddManagerDialog);
